import React, {
  useEffect,
} from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import SurveyStepFooter from 'Views/Onboarding/Pages/Survey/SurveyStep/SurveyStepFooter';
import SurveyStepHeader from 'Views/Onboarding/Components/SurveyStepHeader';
import {
  FDBodyText,
  FDDialog,
  FDDialogContent,
  FDDialogTitle,
  FDPrimaryActionButton,
  FDStack,
  FDTextButton,
} from '@facet-wealth/design';
import fr from 'facet-rest';
import FileUploadDropAreaWithAttachmentList
  from 'Components/ComponentLibrary/Molecules/FileUploadDropAreaWithAttachmentList';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { noop } from 'Helpers/PropTypes';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { useSurveyContext } from 'Views/Onboarding/Pages/Survey/SurveyContext';
import { useSurveyStepContext } from 'Views/Onboarding/Pages/Survey/SurveyStep/SurveyStepContext';
import useMobileLayout
  from 'Hooks/useMobileLayout';
import {
  useSnackbarContext,
} from 'Context/SnackbarContext';

export const IncomeSalaryPayStubUploadLayout = () => {
  const { refreshOnboardingStepCollection, setOnboardingStepCollection } = useSurveyContext();
  const { onClickNext, isSubmitting } = useSurveyStepContext();
  const { displayErrorMessage } = useSnackbarContext();
  const isMobile = useMobileLayout();

  const [dialogOpen, setDialogOpen] = React.useState(true);

  const { logEvent, logEventSuccess, logEventFailure } = useEventTrackingLogEvent({ nodeName: 'Income Salary onboarding step' });
  const {
    resource,
    onSubmit: onSubmitMetaForm,
  } = useMetaLayoutContext();

  useEffect(() => {
    setDialogOpen(true);
  }, [resource.instanceId()]);

  const uploadingState = React.useState(false);
  const [uploading] = uploadingState;

  const logRelationshipEvent = (eventName, logEventFn = logEvent) => resource.withRelationship(
    fr.relationships.upload.name,
    (rel) => logEventFn(
      eventName,
      {
        relevantFeatureToggles: rel.meta('relevantFeatureToggles'),
      },
    ),
    noop,
  );

  const handleUploadComplete = async (success) => {
    await refreshOnboardingStepCollection();

    if (success) {
      logRelationshipEvent('Income Salary onboarding step pay stub upload success', logEventSuccess);
      return;
    }

    logRelationshipEvent('Income Salary onboarding step pay stub failure', logEventFailure);
  };

  useComponentDidMount(() => {
    logRelationshipEvent('Income Salary onboarding step loaded');
  });

  return (
    <FDStack direction={'column'} alignItems={'center'} spacing={2} maxWidth={500}>
      <SurveyStepHeader
        title={resource.attr('title')}
        subtitle={resource.attr('subtitle')}
      />
      {resource.withRelationship(fr.relationships.upload.name,
        (rel) => (
          <FileUploadDropAreaWithAttachmentList
            trailingTitleText={'pay stubs'}
            subtitle={rel.meta('description')}
            url={rel.link('related').url()}
            acceptedFileTypes="image/*,.pdf"
            attachments={resource.attr('payStubs').resources().list()}
            onSuccess={() => handleUploadComplete(true)}
            onError={() => handleUploadComplete(false)}
            onDelete={refreshOnboardingStepCollection}
            onUploadEnd={() => handleUploadComplete(true)}
            uploadingState={uploadingState}
            confirmOnDelete
          />
        ),
        () => null)}
      {resource.attr('payStubs').resources().length() === 0 && resource.attr('primaryActionLabelNoPayStub') ? (
        <FDTextButton
          disabled={isSubmitting || uploading}
          alignSelf={'center'}
          appearance={'standalone'}
          label={resource.attr('primaryActionLabelNoPayStub')}
          onClick={() => {
            return onClickNext(onSubmitMetaForm);
          }}
        />
      ) : null}
      <SurveyStepFooter
        onSubmit={onSubmitMetaForm}
        NextButtonProps={{
          buttonLabel: resource.attr('primaryActionLabel'),
          disabled: uploading,
        }}
      />
      {resource.withRelationship(
        fr.relationships.skipUploadConfirmationFeature.name,
        (_) => (
          <FDDialog
            open={dialogOpen}
            onClose={() => {
              setDialogOpen(false);
            }}
            dialogWidth={'sm'}
            color={'subtle'}
            fullScreen={isMobile}
            closeOnBackdropClick
          >
            <FDDialogTitle value={resource.attr('confirmationTitle')} />
            <FDDialogContent>
              <FDStack direction="column" spacing={2} alignItems={'center'}>
                <FDBodyText value={resource.attr('confirmationSubtitle')} />
                <FDPrimaryActionButton label="Upload a paystub" onClick={() => setDialogOpen(false)} />
                {resource.withRelationship(
                  fr.relationships.skipUploadConfirmation.name,
                  (rel) => (
                    <FDTextButton
                      label={rel.meta('prompt')}
                      onClick={async () => {
                        try {
                          setOnboardingStepCollection(await rel.link('related').post());
                        } catch (err) {
                          displayErrorMessage(err, err.errors ?? 'Error submitting');
                        }
                      }}
                    />
                  ),
                  noop,
                )}
              </FDStack>
            </FDDialogContent>
          </FDDialog>
        ),
        noop,
      )}
    </FDStack>
  );
};
